import axios from "axios";
import BASE_URL from "../../config";

// Get the auth token from localStorage and return headers
const getAuthHeader = () => {
  const tokenData = JSON.parse(localStorage.getItem("authUser") || "{}");
  return {
    Authorization: `Bearer ${tokenData.accessToken}`,
    'Content-Type': 'application/json'
  };
};

// Get next receipt number
export const getNextReceiptNumber = async (businessId) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/api/Receipt/get-next-receipt-number`,
      { BusinessId: businessId },
      { headers: getAuthHeader() }
    );
    return response.data;
  } catch (error) {
    console.error("Error getting next receipt number:", error);
    throw error;
  }
};

// Get customer credit invoices
export const getCustomerCreditInvoices = async (customerId, businessId, branchId = null) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/api/Receipt/get-customer-credit-invoices`,
      {
        CustomerId: customerId,
        BusinessId: businessId,
        BranchId: branchId
      },
      { headers: getAuthHeader() }
    );
    return response.data;
  } catch (error) {
    console.error("Error getting customer credit invoices:", error);
    throw error;
  }
};

// Save receipt
export const saveReceipt = async (receiptData) => {
  try {
    const headers = getAuthHeader();
    
    // If receiptData is FormData (for file upload), don't set Content-Type
    if (receiptData instanceof FormData) {
      const response = await axios.post(
        `${BASE_URL}/api/Receipt/save-receipt`,
        receiptData,
        {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } else {
      // For regular JSON data
      const response = await axios.post(
        `${BASE_URL}/api/Receipt/save-receipt`,
        receiptData,
        {
          headers: {
            ...headers,
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    }
  } catch (error) {
    console.error("Error saving receipt:", error);
    throw error;
  }
};

// Get receipts with filters and pagination
export const getReceipts = async (filter = {}) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/api/Receipt/get-receipts`,
      filter,
      { headers: getAuthHeader() }
    );
    return response.data;
  } catch (error) {
    console.error("Error getting receipts:", error);
    throw error;
  }
};

// Get receipt by ID
export const getReceiptById = async (receiptId) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/api/Receipt/get-receipt-by-id/${receiptId}`,
      { headers: getAuthHeader() }
    );
    return response.data;
  } catch (error) {
    console.error("Error getting receipt by ID:", error);
    throw error;
  }
};

// API to export customer receipts summary to Excel
export const exportCustomerReceiptsSummaryToExcel = async (filter = {}) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/Receipt/export-customer-receipts-summary-excel`, filter, {
      headers: getAuthHeader(),
      responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const fromDateStr = filter.FromDate ? new Date(filter.FromDate).toISOString().slice(0,10).replace(/-/g, '') : 'All';
    const toDateStr = filter.ToDate ? new Date(filter.ToDate).toISOString().slice(0,10).replace(/-/g, '') : 'All';
    link.setAttribute('download', `CustomerReceiptsSummary_${fromDateStr}_to_${toDateStr}_${new Date().toISOString().slice(0,19).replace(/[-:]/g, '').replace('T', '_')}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error("Error exporting customer receipts summary:", error);
    throw error;
  }
};

// API to export customer receipts details to Excel
export const exportCustomerReceiptsDetailsToExcel = async (filter = {}) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/Receipt/export-customer-receipts-details-excel`, filter, {
      headers: getAuthHeader(),
      responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const fromDateStr = filter.FromDate ? new Date(filter.FromDate).toISOString().slice(0,10).replace(/-/g, '') : 'All';
    const toDateStr = filter.ToDate ? new Date(filter.ToDate).toISOString().slice(0,10).replace(/-/g, '') : 'All';
    link.setAttribute('download', `CustomerReceiptsDetails_${fromDateStr}_to_${toDateStr}_${new Date().toISOString().slice(0,19).replace(/[-:]/g, '').replace('T', '_')}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error("Error exporting customer receipts details:", error);
    throw error;
  }
};

// API to export cash deposits summary to Excel
export const exportCashDepositsSummaryToExcel = async (filter = {}) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/Receipt/export-cash-deposits-summary-excel`, filter, {
      headers: getAuthHeader(),
      responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const fromDateStr = filter.FromDate ? new Date(filter.FromDate).toISOString().slice(0,10).replace(/-/g, '') : 'All';
    const toDateStr = filter.ToDate ? new Date(filter.ToDate).toISOString().slice(0,10).replace(/-/g, '') : 'All';
    link.setAttribute('download', `CashDepositsSummary_${fromDateStr}_to_${toDateStr}_${new Date().toISOString().slice(0,19).replace(/[-:]/g, '').replace('T', '_')}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error("Error exporting cash deposits summary:", error);
    throw error;
  }
};

// API to export cash deposits details to Excel
export const exportCashDepositsDetailsToExcel = async (filter = {}) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/Receipt/export-cash-deposits-details-excel`, filter, {
      headers: getAuthHeader(),
      responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const fromDateStr = filter.FromDate ? new Date(filter.FromDate).toISOString().slice(0,10).replace(/-/g, '') : 'All';
    const toDateStr = filter.ToDate ? new Date(filter.ToDate).toISOString().slice(0,10).replace(/-/g, '') : 'All';
    link.setAttribute('download', `CashDepositsDetails_${fromDateStr}_to_${toDateStr}_${new Date().toISOString().slice(0,19).replace(/[-:]/g, '').replace('T', '_')}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error("Error exporting cash deposits details:", error);
    throw error;
  }
};
